/**
 * Which public site we're on. The same app answers at two addresses:
 * - civilprocesssoftware.com: CivilHound (and CivilLogix), civil process only.
 * - 803systems.com: BlueHQ, the records management system (civil process is one of its modules).
 * For testing, add ?site=rms (or ?site=civil) to any address; it sticks for the browser tab.
 */
export type Site = 'rms' | 'civil';

export function site(): Site {
  try {
    const q = new URLSearchParams(location.search).get('site');
    if (q === 'rms' || q === 'civil') sessionStorage.setItem('ch-site', q);
    const s = sessionStorage.getItem('ch-site');
    if (s === 'rms' || s === 'civil') return s;
  } catch {
    /* storage blocked: go by the address */
  }
  return /(^|\.)803systems\.com$/i.test(location.hostname) ? 'rms' : 'civil';
}

export const isRmsSite = () => site() === 'rms';

/** What the signed-in agency bought ('bluehq' = everything, 'civilhound' = civil process only). Set when the session loads. */
export type Product = 'bluehq' | 'civilhound';
let agencyProduct: Product | null = null;
/** Signed in: the agency's product. Signed out: whichever website this is. */
export const currentProduct = (): Product => agencyProduct ?? (isRmsSite() ? 'bluehq' : 'civilhound');
/** <html data-product="…"> picks the product's look in styles.css. */
export const markProduct = () => {
  try {
    document.documentElement.dataset.product = currentProduct();
  } catch {
    /* not in a browser */
  }
};
export const setAgencyProduct = (p: Product | null | undefined) => {
  agencyProduct = p ?? null;
  markProduct();
};
export const isBlueHQ = () => currentProduct() === 'bluehq';
/** Product name for the header and titles. */
export const productName = () => (isBlueHQ() ? 'BlueHQ' : 'CivilHound');

/** Screens that are part of BlueHQ (hidden for CivilHound agencies; the server refuses them too). */
const BLUEHQ_SCREENS = /^\/(field\/)?(messages|reports|evidence|photos|warrants|cases|investigations|directory|org|fleet|equipment|spares|inventory|my-gear|court|case-files|permits|tows|tickets|home|civil-search|evictions|on-call|phones|training|pio|tips|intel|uof|personnel|ews|hr|evaluations|inspections|narcan|alerts|schools|forms|escorts|bolos|applicants|bwc|events|arrests|shutoffs|policies|mileage|call-numbers|dues|house-checks|daily-logs?|ia|command|resources|patrol-map)(\/|\?|$)/;
export const screenInProduct = (path: string) => isBlueHQ() || !BLUEHQ_SCREENS.test(path);
